/**
 * What the Documents screen says about Google Drive, as data rather than JSX.
 *
 * The sibling of `document-processing-view.ts`, and for the same reason: importing from Drive
 * is almost entirely wording — a button, one refusal sentence, a dialog's two lines, a strip
 * reporting per file, a badge and a menu item — and wording that lives in components is
 * wording nobody can test. Everything below is pure; the components place strings.
 */

import { type GoogleExportFormat, resolveGoogleEditorFormat } from "@repo/shared/google-workspace";
import type { ProjectDocumentRow } from "./document-row-view";

/** The provenance the backend joins for an imported row: identity plus what Google said. */
export type DocumentExternalSource = NonNullable<ProjectDocumentRow["externalSource"]>;

/**
 * What one Drive file became. The three outcomes are the import action's own, and the reason
 * they are three: a file this project already holds is not an error and not a new document.
 */
export type GoogleImportOutcome = "imported" | "reused" | "drift";

export type GoogleImportItemState =
  | { kind: "importing" }
  | { kind: "done"; outcome: GoogleImportOutcome }
  /**
   * `code` is the coded refusal behind the sentence, kept for the caller that can offer a repair
   * the strip cannot: a pasted link whose file the grant does not cover is answered by the Picker,
   * not by a message. Absent where the failure carried no code.
   */
  | { kind: "failed"; message: string; code?: string };

export type GoogleImportItem = {
  /** The Drive file id — stable across the whole batch, which the artifact id is not. */
  fileId: string;
  name: string;
  state: GoogleImportItemState;
};

/** Every fixed string this feature shows, in one object. */
export const GOOGLE_IMPORT_COPY = {
  /** The button beside "Upload files", wherever that button is offered. */
  addFromDrive: "Add from Google Drive",
  /** No Google client on this deployment: nobody using the screen can do anything about it. */
  notConfigured: "Google Drive import is not set up for this environment",
  /** The toast's action after a press that failed in the browser: the same press, once more. */
  tryAgain: "Try again",
  /**
   * The provider itself, in the same short vocabulary the editor types use — for a file that
   * merely *lives* in Drive, where no editor owns the document.
   */
  driveShortLabel: "G Drive",
  /**
   * The ⋯ menu items, both built from the type's own short noun (`resolveGoogleOpenLabel`,
   * `resolveGoogleRefreshLabel`): "Open G Doc" reaches the document, "Refresh from G Doc" asks
   * Google whether it has moved and reads it again if so.
   */
  openVerb: "Open",
  refreshVerb: "Refresh from",
  /** Nothing had changed, and the check just happened — the relative time would say "0 s ago". */
  refreshUpToDate: "Up to date · checked just now",
  /** A new version is being read; the row's own states take the reporting from here. */
  refreshUpdated: "Newer version found · reading it now",
  /** The provider could not be asked at all. Said without a repair, because there isn't one. */
  refreshCheckFailed: "Last check failed",
  dismiss: "Dismiss",
} as const;

/** The per-file sentence beside the name in the import strip. `null` where the glyph says it. */
const GOOGLE_IMPORT_ITEM_DETAIL: Record<GoogleImportItemStatus, string | null> = {
  importing: "Importing from Google Drive…",
  imported: null,
  reused: "Already in this project",
  drift: "Already in this project · a newer version exists in Google Drive",
  failed: null,
};

export type GoogleImportItemStatus = "importing" | GoogleImportOutcome | "failed";

export type GoogleImportItemView = {
  fileId: string;
  name: string;
  status: GoogleImportItemStatus;
  /**
   * The sentence beside the name, or `null` for a plain success — the row is in the table by
   * then, and repeating "added" here would be the duplicate-list problem this screen avoids.
   */
  detail: string | null;
};

export type GoogleImportBatchView = {
  /** "Importing 3 documents from Google Drive" / "Added 2 documents from Google Drive". */
  title: string;
  /** The counts under it: "1 of 3 done", "2 added · 1 already in this project". */
  detail: string;
  /** Something is still in flight — the strip spins and offers no dismissal. */
  isBusy: boolean;
  items: GoogleImportItemView[];
  dismissLabel: string;
};

function documents(count: number): string {
  return count === 1 ? "1 document" : `${count} documents`;
}

function statusOf(state: GoogleImportItemState): GoogleImportItemStatus {
  if (state.kind === "importing") return "importing";
  if (state.kind === "failed") return "failed";
  return state.outcome;
}

export function buildGoogleImportItemView(item: GoogleImportItem): GoogleImportItemView {
  const status = statusOf(item.state);
  return {
    fileId: item.fileId,
    name: item.name,
    status,
    // A failure speaks in the words the backend gave it (already user-safe through
    // `getErrorMessage`); every other state has one fixed sentence.
    detail: item.state.kind === "failed" ? item.state.message : GOOGLE_IMPORT_ITEM_DETAIL[status],
  };
}

/**
 * The whole strip, or `null` when there is nothing left worth a strip.
 *
 * Two ways to reach `null`, and the second is the interesting one: a batch in which every file
 * became a new document has nothing to disclose that the table and the batch-ready bar below it
 * do not already say better, so it hands the slot over instead of asking to be dismissed. A
 * batch holding a reuse, a drift or a failure stays until somebody has read it.
 */
export function resolveGoogleImportBatch(input: {
  items: readonly GoogleImportItem[];
  acknowledged: boolean;
}): GoogleImportBatchView | null {
  const { items, acknowledged } = input;
  if (items.length === 0 || acknowledged) return null;

  const importingCount = items.filter((item) => item.state.kind === "importing").length;
  const isBusy = importingCount > 0;

  const importedCount = items.filter(
    (item) => item.state.kind === "done" && item.state.outcome === "imported"
  ).length;
  const knownCount = items.filter(
    (item) =>
      item.state.kind === "done" &&
      (item.state.outcome === "reused" || item.state.outcome === "drift")
  ).length;
  const failedCount = items.filter((item) => item.state.kind === "failed").length;

  if (!isBusy && knownCount === 0 && failedCount === 0) return null;

  const doneCount = items.length - importingCount;
  const parts: string[] = [];
  if (importedCount > 0) parts.push(`${importedCount} added`);
  if (knownCount > 0) parts.push(`${knownCount} already in this project`);
  if (failedCount > 0) parts.push(`${failedCount} failed`);

  return {
    title: isBusy
      ? `Importing ${documents(items.length)} from Google Drive`
      : `Added ${documents(importedCount)} from Google Drive`,
    detail: isBusy ? `${doneCount} of ${items.length} done` : parts.join(" · "),
    isBusy,
    items: items.map(buildGoogleImportItemView),
    dismissLabel: GOOGLE_IMPORT_COPY.dismiss,
  };
}

/** Which Google editor holds this document, or `undefined` for a file Drive merely stored. */
export function resolveGoogleEditor(
  externalSource: DocumentExternalSource
): GoogleExportFormat | undefined {
  return resolveGoogleEditorFormat(externalSource.googleMimeType);
}

/**
 * What the TYPE badge says for a document Google owns, in the width a table cell has: `G Doc`,
 * not "Google Docs". `null` for a file Drive merely stored — a PDF picked from Drive is a PDF,
 * and its own extension says more than the place it was picked from.
 */
export function resolveGoogleSourceLabel(externalSource: DocumentExternalSource): string | null {
  return resolveGoogleEditor(externalSource)?.shortLabel ?? null;
}

/**
 * Whether Google *owns* this document rather than merely storing a file of it.
 *
 * The one question the row's own behaviour turns on: the name opens the document at Google, the
 * ⋯ menu offers no download of the export taken to read it, and a rename is held to no
 * extension. False for a PDF picked from Drive, whose bytes are the document.
 */
export function isGoogleOwnedDocument(externalSource: DocumentExternalSource): boolean {
  return resolveGoogleEditor(externalSource) !== undefined;
}

/**
 * The ⋯ menu item that reaches the document at Google: "Open G Doc" for a document an editor
 * owns, "Open in G Drive" for a file that merely lives there.
 *
 * Built from the type's own noun rather than written out per type, so the badge, this item and
 * the refresh beneath it cannot come to call the same document three different things — and the
 * preposition follows the object: an editor *is* opened, a drive is opened *in*.
 */
export function resolveGoogleOpenLabel(externalSource: DocumentExternalSource): string {
  const editor = resolveGoogleEditor(externalSource);
  return editor
    ? `${GOOGLE_IMPORT_COPY.openVerb} ${editor.shortLabel}`
    : `${GOOGLE_IMPORT_COPY.openVerb} in ${GOOGLE_IMPORT_COPY.driveShortLabel}`;
}

/** The item under it, named for where it asks: "Refresh from G Sheet", "Refresh from G Drive". */
export function resolveGoogleRefreshLabel(externalSource: DocumentExternalSource): string {
  const editor = resolveGoogleEditor(externalSource);
  return `${GOOGLE_IMPORT_COPY.refreshVerb} ${editor?.shortLabel ?? GOOGLE_IMPORT_COPY.driveShortLabel}`;
}
